import React, { useCallback, useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import { slugify } from "../../utils/reading";
import { uploadImage } from "../../utils/uploadImage";
import { heroCropStyle } from "../../utils/image";
import { PUBLISH_WORKER_URL } from "../../config";
import CKEditorField from "./CKEditorField";
import PostPreview from "./PostPreview";
import "./Admin.css";

const emptyPost = {
  slug: "",
  title: "",
  summary: "",
  category: "Blog Yazıları",
  image: "",
  imageCrop: null,
  date: new Date().toISOString().split("T")[0],
  readTime: "",
  author: "",
  editor: "",
  tags: "",
  featured: false,
  published: true,
  content: "",
};

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (err) {
      setError("Giriş başarısız. E-posta veya şifre hatalı.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-login">
      <form className="admin-login-box" onSubmit={handleSubmit}>
        <h1>Lingify Blog Yönetimi</h1>
        <p>Devam etmek için giriş yapın.</p>
        <input
          type="email"
          placeholder="E-posta"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="Şifre"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {error && <div className="admin-error">{error}</div>}
        <button type="submit" disabled={busy}>
          {busy ? "Giriş yapılıyor..." : "Giriş Yap"}
        </button>
      </form>
    </div>
  );
};

const Editor = ({ initial, onSaved, onCancel }) => {
  const isNew = !initial.slug;
  const [form, setForm] = useState({
    ...emptyPost,
    ...initial,
    tags: Array.isArray(initial.tags) ? initial.tags.join(", ") : initial.tags || "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(false);
  const coverRef = useRef(null);
  const [coverBusy, setCoverBusy] = useState(false);

  const handleCover = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setCoverBusy(true);
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, image: url }));
    } catch (err) {
      setError("Kapak yüklenemedi: " + err.message);
    } finally {
      setCoverBusy(false);
      e.target.value = "";
    }
  };

  const [cropXY, setCropXY] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const onCropComplete = useCallback((area) => {
    setForm((f) => ({ ...f, imageCrop: area }));
  }, []);

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleTitle = (e) => {
    const title = e.target.value;
    setForm((f) => ({
      ...f,
      title,
      slug: isNew ? slugify(title) : f.slug,
    }));
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.slug.trim()) {
      setError("Başlık ve slug zorunlu.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const data = {
        slug: form.slug.trim(),
        title: form.title.trim(),
        summary: form.summary.trim(),
        category: form.category.trim() || "Blog Yazıları",
        image: form.image.trim(),
        imageCrop: form.imageCrop || null,
        date: form.date,
        readTime: form.readTime.trim(),
        author: form.author.trim(),
        editor: form.editor.trim(),
        tags: form.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        featured: !!form.featured,
        published: !!form.published,
        content: form.content,
      };
      await setDoc(doc(db, "posts", data.slug), data);
      onSaved();
    } catch (err) {
      setError("Kaydedilemedi: " + err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-editor">
      <div className="admin-editor-head">
        <h2>{isNew ? "Yeni Yazı" : "Yazıyı Düzenle"}</h2>
        <div className="admin-editor-actions">
          <button className="btn-ghost" onClick={onCancel}>Vazgeç</button>
          <button className="btn-ghost" onClick={() => setPreview(true)}>Önizleme</button>
          <button className="btn-primary" onClick={handleSave} disabled={busy}>
            {busy ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </div>

      {preview && <PostPreview post={form} onClose={() => setPreview(false)} />}

      {error && <div className="admin-error">{error}</div>}

      <label>Başlık</label>
      <input value={form.title} onChange={handleTitle} placeholder="Yazı başlığı" />

      <label>Slug (URL) {isNew ? "" : "— oluşturulduktan sonra değiştirilemez"}</label>
      <input value={form.slug} onChange={set("slug")} disabled={!isNew} placeholder="yazi-basligi" />

      <div className="admin-row">
        <div>
          <label>Kategori</label>
          <input value={form.category} onChange={set("category")} list="kategoriler" />
          <datalist id="kategoriler">
            <option value="Blog Yazıları" />
            <option value="Makale" />
            <option value="Haberler" />
            <option value="Bültenler" />
          </datalist>
        </div>
        <div>
          <label>Tarih</label>
          <input type="date" value={form.date} onChange={set("date")} />
        </div>
      </div>

      <label>Özet</label>
      <textarea value={form.summary} onChange={set("summary")} rows="2" placeholder="Kısa özet (kartlarda ve SEO'da görünür)" />

      <div className="admin-row">
        <div>
          <label>Yazar</label>
          <input value={form.author} onChange={set("author")} placeholder="Yazar adı" />
        </div>
        <div>
          <label>Editör (opsiyonel)</label>
          <input value={form.editor} onChange={set("editor")} placeholder="Editör adı" />
        </div>
      </div>

      <label>Kapak görseli</label>
      <div className="admin-cover">
        <input value={form.image} onChange={set("image")} placeholder="Görsel yükle ya da URL yapıştır" />
        <button type="button" className="btn-ghost" onClick={() => coverRef.current && coverRef.current.click()}>
          {coverBusy ? "Yükleniyor..." : "Yükle"}
        </button>
        <input ref={coverRef} type="file" accept="image/*" hidden onChange={handleCover} />
      </div>
      {form.image && (
        <div className="admin-crop">
          <p className="admin-focus-hint">
            Görseli sürükle ve yakınlaştır — çerçevede ne görünürse detay sayfasında hero'da o görünür.
          </p>
          <div className="admin-crop-row">
            <div className="admin-crop-col">
              <div className="admin-crop-area">
                <Cropper
                  image={form.image}
                  crop={cropXY}
                  zoom={zoom}
                  aspect={2.4}
                  minZoom={0.5}
                  maxZoom={3}
                  zoomSpeed={0.15}
                  objectFit="cover"
                  restrictPosition={false}
                  showGrid={false}
                  onCropChange={setCropXY}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  initialCroppedAreaPercentages={form.imageCrop || undefined}
                />
              </div>
              <div className="admin-zoom">
                <span className="admin-zoom-label">Yakınlaştırma</span>
                <input
                  type="range"
                  min="0.5"
                  max="3"
                  step="0.02"
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                />
              </div>
            </div>
            <div className="admin-crop-preview">
              <span className="admin-focus-live-label">Detayda böyle görünecek</span>
              <div className="admin-crop-preview-box">
                <img src={form.image} alt="" style={heroCropStyle(form.imageCrop)} />
              </div>
            </div>
          </div>
        </div>
      )}

      <label>Etiketler (virgülle ayır)</label>
      <input value={form.tags} onChange={set("tags")} placeholder="afazi, inme, dil terapisi" />

      <label>İçerik</label>
      <CKEditorField
        value={form.content}
        onChange={(html) => setForm((f) => ({ ...f, content: html }))}
      />

      <div className="admin-checks">
        <label className="admin-check">
          <input type="checkbox" checked={form.featured} onChange={set("featured")} /> Öne çıkan
        </label>
        <label className="admin-check">
          <input type="checkbox" checked={form.published} onChange={set("published")} /> Yayında
        </label>
      </div>
    </div>
  );
};

const Dashboard = ({ user }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // post object or {} for new, null for list
  const [publishing, setPublishing] = useState(false);
  const [publishMsg, setPublishMsg] = useState("");

  const handlePublish = async () => {
    setPublishing(true);
    setPublishMsg("");
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(PUBLISH_WORKER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });
      if (!res.ok) throw new Error();
      setPublishMsg("Yayınlama başlatıldı — site birkaç dakika içinde güncellenecek.");
    } catch {
      setPublishMsg("Yayınlama başlatılamadı. Lütfen tekrar deneyin.");
    } finally {
      setPublishing(false);
    }
  };

  const load = async () => {
    setLoading(true);
    const snap = await getDocs(collection(db, "posts"));
    const list = snap.docs
      .map((d) => d.data())
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    setPosts(list);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (slug) => {
    if (!window.confirm("Bu yazı silinsin mi?")) return;
    await deleteDoc(doc(db, "posts", slug));
    load();
  };

  if (editing !== null) {
    return (
      <Editor
        initial={editing}
        onSaved={() => {
          setEditing(null);
          load();
        }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-top">
        <h1>Blog Yazıları</h1>
        <div className="admin-top-actions">
          <span className="admin-user">{user.email}</span>
          <button className="btn-publish" onClick={handlePublish} disabled={publishing}>
            {publishing ? "Yayınlanıyor..." : "Siteyi Güncelle"}
          </button>
          <button className="btn-primary" onClick={() => setEditing({})}>+ Yeni Yazı</button>
          <button className="btn-ghost" onClick={() => signOut(auth)}>Çıkış</button>
        </div>
      </div>

      {publishMsg && <div className="admin-publish-msg">{publishMsg}</div>}

      {loading ? (
        <p className="admin-muted">Yükleniyor...</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Başlık</th>
              <th>Kategori</th>
              <th>Tarih</th>
              <th>Durum</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.slug}>
                <td>{p.title}</td>
                <td>{p.category}</td>
                <td>{p.date}</td>
                <td>
                  {p.published === false ? (
                    <span className="tag-draft">Taslak</span>
                  ) : (
                    <span className="tag-live">Yayında</span>
                  )}
                  {p.featured && <span className="tag-featured">Öne çıkan</span>}
                </td>
                <td className="admin-actions-cell">
                  <button className="btn-link" onClick={() => setEditing(p)}>Düzenle</button>
                  <button className="btn-link danger" onClick={() => handleDelete(p.slug)}>Sil</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="admin-note">
        Not: Yazıyı kaydettikten sonra "Siteyi Güncelle"ye bas — site birkaç dakika içinde yenilenir.
      </p>
    </div>
  );
};

const Admin = () => {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => setUser(u));
  }, []);

  if (user === undefined) {
    return <div className="admin-loading">Yükleniyor...</div>;
  }

  return (
    <div className="admin-wrap">
      {user ? <Dashboard user={user} /> : <Login />}
    </div>
  );
};

export default Admin;
